import React, { useMemo } from 'react';
import { Column } from './LgmDataTable';
import { LgmDataTableType } from './LgmDataTable';
import { Plus, Minus } from 'lucide-react';
import { BUTTON_PRIMARY } from '../theme/colors';
import { GRAY_300 } from 'theme/grayScale';

export const TABLE_CELL_MAX_LENGTH = 100;

export type TableCellProps<T> = {
  row: T;
  col: Column<T>;
  colIndex: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  customCellStyle?: (
    row: T,
    col: Column<T>,
    colIndex: number
  ) => React.CSSProperties;
};

const TableCell = <T extends LgmDataTableType>(props: TableCellProps<T>) => {
  const { row, col, colIndex, isExpanded, onToggleExpand } = props;

  const cell = useMemo(() => {
    let cell: React.ReactNode;
    if (col.render) {
      cell = col.render(row, isExpanded);
    } else {
      const rawValue = row[col.key];
      cell = rawValue === null ? '' : String(rawValue);
    }
    if (cell === undefined || cell === null || cell === '') {
      cell = '';
    }
    return cell;
  }, [row, col, isExpanded]);

  const isCollapsible = useMemo(() => {
    const textLength = col.textLength
      ? col.textLength(row)
      : String(cell).length;
    return textLength > TABLE_CELL_MAX_LENGTH;
  }, [col, row, cell]);

  return (
    <td
      key={colIndex}
      style={{
        ...styles.mainTableCellStyle,
        position: 'relative', // Enable positioning for the span
      }}>
      {isCollapsible && (
        <span style={styles.expandCollapseIcon} onClick={onToggleExpand}>
          {isExpanded ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      )}
      <div
        style={{
          ...(isCollapsible
            ? isExpanded
              ? styles.cellExpanded
              : styles.cellCollapsed
            : {}),
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingLeft: '8px',
          paddingRight: isCollapsible ? '24px' : '8px', // Reserve space for icon or normal padding
          wordWrap: 'break-word' as const,
          ...(props.customCellStyle
            ? props.customCellStyle(row, col, colIndex)
            : {}),
        }}>
        {cell}
      </div>
    </td>
  );
};

const styles: Record<string, React.CSSProperties> = {
  mainTableCellStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '0px',
  } as React.CSSProperties,
  expandCollapseIcon: {
    cursor: 'pointer',
    position: 'absolute', // Position the span absolutely within the cell
    top: '4px', // Align to the top of the cell
    right: '4px', // Align to the right of the cell
    zIndex: 1, // Ensure it appears above the cell content
    color: BUTTON_PRIMARY.rest, // matches the original #2563eb, not COLOR_INFO's #3b82f6
  } as React.CSSProperties,
  cellExpanded: {
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,
  cellCollapsed: {
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,
};

export default TableCell;
